import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { FieldError } from "@/components/auth/login-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { meQueryKey } from "@/lib/auth";
import { accountApi } from "@/lib/users";

export const Route = createFileRoute("/_authenticated/account")({
  component: AccountPage,
});

const profileSchema = z.object({
  first_name: z.string().max(150),
  last_name: z.string().max(150),
});

const passwordSchema = z
  .object({
    current_password: z.string().min(1, "Enter your current password"),
    new_password: z.string().min(8, "Use at least 8 characters"),
    confirm: z.string(),
  })
  .refine((v) => v.new_password === v.confirm, {
    message: "Passwords do not match",
    path: ["confirm"],
  });

function AccountPage() {
  const { user } = Route.useRouteContext();
  const queryClient = useQueryClient();

  const profileForm = useForm<z.infer<typeof profileSchema>>({
    resolver: zodResolver(profileSchema),
    defaultValues: { first_name: user.first_name, last_name: user.last_name },
  });
  const saveProfile = useMutation({
    mutationFn: accountApi.updateProfile,
    onSuccess: (updated) => {
      queryClient.setQueryData(meQueryKey, updated);
      toast.success("Profile updated");
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Could not save"),
  });

  const passwordForm = useForm<z.infer<typeof passwordSchema>>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { current_password: "", new_password: "", confirm: "" },
  });
  const changePassword = useMutation({
    mutationFn: accountApi.changePassword,
    onSuccess: () => {
      passwordForm.reset();
      toast.success("Password changed");
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        const errors = error.fieldErrors;
        if (errors.current_password)
          passwordForm.setError("current_password", { message: errors.current_password[0] });
        if (errors.new_password)
          passwordForm.setError("new_password", { message: errors.new_password.join(" ") });
        if (!errors.current_password && !errors.new_password) toast.error(error.message);
      } else {
        toast.error("Could not change password");
      }
    },
  });

  return (
    <div className="max-w-lg space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Account</h1>

      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
          <CardDescription>{user.email}</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={profileForm.handleSubmit((v) => saveProfile.mutate(v))}
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First name</Label>
                <Input id="first_name" {...profileForm.register("first_name")} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last name</Label>
                <Input id="last_name" {...profileForm.register("last_name")} />
              </div>
            </div>
            <Button type="submit" disabled={saveProfile.isPending}>
              Save
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Password</CardTitle>
          <CardDescription>
            You stay signed in after changing it.
            {user.session_expires_at &&
              ` Your current session ends ${new Date(user.session_expires_at).toLocaleString()}.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={passwordForm.handleSubmit((v) => changePassword.mutate(v))}
          >
            <div className="space-y-2">
              <Label htmlFor="current_password">Current password</Label>
              <Input
                id="current_password"
                type="password"
                autoComplete="current-password"
                {...passwordForm.register("current_password")}
              />
              <FieldError message={passwordForm.formState.errors.current_password?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password">New password</Label>
              <Input
                id="new_password"
                type="password"
                autoComplete="new-password"
                {...passwordForm.register("new_password")}
              />
              <FieldError message={passwordForm.formState.errors.new_password?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                {...passwordForm.register("confirm")}
              />
              <FieldError message={passwordForm.formState.errors.confirm?.message} />
            </div>
            <Button type="submit" disabled={changePassword.isPending}>
              Change password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
